import { useState, useRef, useCallback } from 'react';
import Flickity from 'react-flickity-component';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { themeGet } from '@styled-system/theme-get';

import Text from '../../Text';
import PixabayLogo from '../../../../assets/images/pixabay_logo.svg';
import GiphyLogo from '../../../../assets/images/giphy_attribution.png';
import { Button } from '../../FormStyles/Button';
import useMutationObservable from '../../../lib/hooks/useMutationObservable';
import defaultAttrs from '../../../lib/utils/defaultAttrs';

const providerLabels = {
  unsplash: function Unsplash({ t }) {
    return (
      <ProviderLink
        href="https://www.unsplash.com"
        target="_blank"
        rel="noopener"
      >
        {t('media.unsplashAttribution')}
      </ProviderLink>
    );
  },
  pixabay: function Pixabay({ t }) {
    return (
      <ProviderLink
        href="https://www.pixabay.com"
        target="_blank"
        rel="noopener"
      >
        {t('media.genericAttribution')}
        <PixaBayLogo alt="Pixabay_logo" src={PixabayLogo} />
      </ProviderLink>
    );
  },
  giphy: function Giphy() {
    return (
      <ProviderLink href="https://www.giphy.com" target="_blank" rel="noopener">
        <img alt="Giphy" style={{ width: '105px' }} src={GiphyLogo} />
      </ProviderLink>
    );
  },
};

const ImageForGrid = ({ onClick, src, alt, lazyLoadData }) => {
  return (
    <ImageResultWrapper onClick={onClick}>
      <ImageResult src={src} alt={alt} data-flickity-lazyload={lazyLoadData} />
    </ImageResultWrapper>
  );
};

const ImageSearch = ({ results, selectImage, closeSearch }) => {
  const carouselRef = useRef();
  const [flickity, setFlickity] = useState(null);
  const [searchProvider, setSearchProvider] = useState('');
  const { t } = useTranslation('tileForm');

  const onCarouselMutation = useCallback(() => {
    if (flickity) {
      const selectedCell = document.querySelector('.is-selected');
      const providerName = selectedCell ? selectedCell.dataset.name : '';
      setSearchProvider(providerName);
    }
  }, [flickity]);

  useMutationObservable(carouselRef.current, onCarouselMutation);

  const { unsplash, pixabay, giphy } = results;

  const hasResults = Object.values(results).some(result => result.length > 0);

  return (
    <>
      {hasResults ? (
        <CarouselWrapper ref={carouselRef}>
          <Flickity
            flickityRef={ref => setFlickity(ref)}
            options={{
              imagesLoaded: false,
              percentPosition: false,
              contain: true,
              pageDots: false,
              groupCells: true,
              draggable: false,
              lazyLoad: true,
            }}
          >
            {unsplash?.map((page, i) => (
              <ResultGrid key={`unsplash-${i}`} data-name="unsplash">
                {page.map(image => (
                  <ImageForGrid
                    key={image.id}
                    onClick={() =>
                      selectImage({
                        url: image.urls.regular,
                        source: 'unsplash',
                        downloadUrl: image.links.download_location,
                        photographerUrl: `${image.user.name}#${image.user.links.html}`,
                      })
                    }
                    src={image.urls.thumb}
                    alt={image.alt_description}
                    lazyLoadData={image.urls.regular}
                  />
                ))}
              </ResultGrid>
            ))}
            {pixabay?.map((page, i) => (
              <ResultGrid data-name="pixabay" key={`pixabay-${i}`}>
                {page.map(image => (
                  <ImageForGrid
                    key={image.id}
                    onClick={() =>
                      selectImage({
                        url: image.webformatURL,
                        source: 'pixabay',
                      })
                    }
                    src={image.previewURL}
                    lazyLoadData={image.previewURL}
                  />
                ))}
              </ResultGrid>
            ))}
            {giphy?.map((page, i) => (
              <ResultGrid data-name="giphy" key={`giphy-${i}`}>
                {page.map(image => (
                  <ImageForGrid
                    key={image.id}
                    onClick={() =>
                      selectImage({
                        url: image.images.original.url,
                        source: 'giphy',
                      })
                    }
                    src={image.images.preview_gif.url}
                    lazyLoadData={image.images.fixed_width_still.url}
                  />
                ))}
              </ResultGrid>
            ))}
          </Flickity>
          <CarouselSubtitle>
            {providerLabels[searchProvider] &&
              providerLabels[searchProvider]({ t })}
          </CarouselSubtitle>
        </CarouselWrapper>
      ) : (
        <Text variant="body2">{t('media.noResults')}</Text>
      )}
      <Button
        variant="containedBlue"
        width="100%"
        mt="20px"
        onClick={closeSearch}
      >
        {t('media.closeSearch')}
      </Button>
    </>
  );
};

export default ImageSearch;

const ResultGrid = styled.div`
  display: flex;
  width: 100%;
  flex-wrap: wrap;
`;

const ImageResultWrapper = styled.div`
  height: 130px;
  width: 130px;
  padding: 5px;
  cursor: pointer;
  display: flex;
`;

const ImageResult = styled.img`
  width: 100%;
  object-fit: cover;
`;

const CarouselWrapper = styled.div`
  width: 100%;
  position: relative;

  .flickity-prev-next-button {
    border: none;
    background: transparent;
    padding: 0;
    color: ${themeGet('colors.black.100')};
  }

  .flickity-prev-next-button:hover {
    color: ${themeGet('colors.black.80')};
  }

  .flickity-prev-next-button:active {
    opacity: 1;
    box-shadow: none;
  }

  .flickity-prev-next-button:disabled {
    display: none;
  }

  .flickity-prev-next-button.previous {
    left: -40px;
  }

  .flickity-prev-next-button.next {
    right: -40px;
  }
`;

const CarouselSubtitle = styled.div`
  position: absolute;
  right: 0;
`;

const PixaBayLogo = styled.img`
  margin-left: 5px;
  width: 70px;
`;

const ProviderLink = styled(Text).attrs(
  defaultAttrs({
    as: 'a',
    variant: 'small4',
  }),
)`
  font-weight: bold;
  display: flex;
  align-items: center;
`;
